// =============================================================================
// useUserManagementHandlers
//
// Responsibility: owns all user-action logic — API calls, state updates,
// and user feedback (toasts).
//
// Pattern mirrors useRoleManagementHandlers:
//   - Receives dispatch via props (no internal Redux reads)
//   - Each handler is a plain function — easy to test in isolation
//   - Returns only what the page component needs to wire up to UI
//   - Uses direct API calls via apiRoute.ts instead of thunks
// =============================================================================

import { useToast } from '../../../../../components/LhToastContainer';
import { userManagementApi } from '../apiRoute';
import {
  setUsers,
  setSelectedUser,
  saveSelectedUser,
  patchUserInList,
  clearSelection,
  setBulkTrainingDate,
  clearNewTags,
  removeUserFromList,
} from '../../../../../store/slices/userManagementSlice';
import type { User, TrainingType } from '../userManagementTypes';
import { TRAINING_LABELS } from '../userManagementTypes';
import { formatDate } from '../userManagementUtils';
import type { AppDispatch } from '../../../../../store';
import type { UserUpdateRequest } from '../userManagementTypes';

export interface UseUserManagementHandlersProps {
  dispatch: AppDispatch;
  refreshCurrentPage: () => void;
}

export const useUserManagementHandlers = ({
  dispatch,
  refreshCurrentPage,
}: UseUserManagementHandlersProps) => {
  const toast = useToast();

  // ─── Toast helper ──────────────────────────────────────────────────────────

  /** Shows a toast notification using the app-wide LhToastProvider. */
  const showToast = (
    title: string,
    message: string,
    type: 'success' | 'error'
  ) => {
    toast.showToast({
      header: title,
      subtext: message,
      variant: type,
      position: 'top',
    });
  };

  // ─── Refresh helper ────────────────────────────────────────────────────────

  /**
   * Reloads all users from the API and updates Redux store.
   * Called after any mutation (update, bulk operations, etc.)
   */
  const refreshUsers = async () => {
    try {
      const users = await userManagementApi.getAll();
      dispatch(setUsers(users));
    } catch (error: any) {
      console.error('[useUserManagementHandlers] Refresh error:', error);
      showToast('Error', error.message || 'Failed to refresh users', 'error');
    }
  };

  // ─── CRUD handlers ─────────────────────────────────────────────────────────

  /**
   * Update a single user.
   * Called from UserDetailPage after validation.
   * On success: refreshes data, shows success toast, returns true.
   * On failure: shows error toast, returns false.
   */
  const handleUpdateUser = async (request: UserUpdateRequest): Promise<boolean> => {
    try {
      console.log('[useUserManagementHandlers] Updating user:', request.user.id);
      
      // 1. Persist to backend
      await userManagementApi.update(request);

      // 2. Re-fetch the user by email (GET /admin/users/by-email) so the table row
      //    reflects exactly what the backend stored. Falls back to the local
      //    edited object if the endpoint returns no usable data.
      //    Always preserve request.user.id so patchUserInList can find the
      //    correct row in state.users (getByEmail uses index=0 which may differ).
      let confirmedUser: User = request.user;
      if (request.userEmail) {
        try {
          const refreshed = await userManagementApi.getByEmail(request.userEmail);
          // Always stamp the original list-row id so the Redux patch finds the right entry
          confirmedUser = { ...refreshed, id: request.user.id };
        } catch {
          // getByEmail failed — fall through to optimistic patch with local object
        }
      }

      // 3. Patch the in-memory list immediately so the row updates without delay
      dispatch(patchUserInList(confirmedUser));

      // 4. Re-fetch the current page so the list reflects server-authoritative data
      refreshCurrentPage();
      
      showToast(
        'Success',
        'User profile updated successfully. Access permissions have been synchronized.',
        'success'
      );
      
      return true;
    } catch (error: any) {
      console.error('[useUserManagementHandlers] Update error:', error);
      showToast('Error', error.message || 'Failed to update user profile.', 'error');
      return false;
    }
  };

  /**
   * Bulk update training dates for multiple users.
   * Called from TrainingModal after user confirms date selection.
   */
  const handleBulkTrainingUpdate = async (
    selectedUsers: User[],
    trainingType: TrainingType,
    date: string | null
  ): Promise<boolean> => {
    try {
      console.log('[useUserManagementHandlers] Bulk training update:', {
        selectedUsers: selectedUsers.length,
        trainingType,
        date,
      });
      
      // Extract emails
      const emails = selectedUsers.map(user => user.userEmail);
      
      // Build payload with current values, only updating the specified training type
      // We need to preserve existing training dates for other fields
      const fundamentals = trainingType === 'fundamentals' ? date : null;
      const lighthouse = trainingType === 'lighthouse' ? date : null;
      const advFundamentals = trainingType === 'advFundamentals' ? date : null;
      
      // Keep newStatus as-is (don't change it during training updates)
      const newStatus = false; // or could preserve existing newStatus from users
      
      await userManagementApi.bulkUpdateTraining(
        emails,
        fundamentals,
        lighthouse,
        advFundamentals,
        newStatus
      );

      // Update affected rows in-place so scroll position and active filters
      // are preserved. Also clears selection (the reducer handles it).
      dispatch(setBulkTrainingDate({ trainingType, date }));

      const label = TRAINING_LABELS[trainingType];
      const formattedDate = date ? formatDate(date) : 'cleared';
      const userCount = selectedUsers.length;
      showToast(
        'Success',
        `${label} completion date set to ${formattedDate} for ${userCount} user${userCount !== 1 ? 's' : ''}.`,
        'success'
      );
      
      return true;
    } catch (error: any) {
      console.error('[useUserManagementHandlers] Bulk training error:', error);
      showToast('Error', error.message || 'Failed to update training dates.', 'error');
      return false;
    }
  };

  /**
   * Clear "new" tags for multiple users.
   * Called from NewUsersModal or individual badge click.
   */
  const handleClearNewTags = async (selectedUsers: User[]): Promise<boolean> => {
    try {
      console.log('[useUserManagementHandlers] Clearing new tags:', selectedUsers.length);
      
      const emails = selectedUsers.map(user => user.userEmail);
      const ids = selectedUsers.map(user => user.id);
      
      await userManagementApi.clearNewTags(emails);
      
      // Update only the affected rows in-place — preserves scroll position
      // and active filters. The reducer also clears selection for bulk actions.
      dispatch(clearNewTags(ids));
      
      const message =
        selectedUsers.length === 1
          ? `New tag ignored for ${selectedUsers[0].userEmail}.`
          : `New tags ignored for ${selectedUsers.length} users.`;
      
      showToast('Success', message, 'success');
      
      return true;
    } catch (error: any) {
      console.error('[useUserManagementHandlers] Clear new tags error:', error);
      showToast('Error', error.message || 'Failed to clear new tags.', 'error');
      return false;
    }
  };

  /**
   * Navigate to user detail view.
   * Sets the user immediately from list data, then fetches the full record
    * by email (GET /admin/users/by-email) to populate the detail page with
   * fresh, complete data.
   */
  const handleSelectUser = async (user: User) => {
    // Optimistic: show detail page right away with list data
    dispatch(setSelectedUser(user));

    if (!user.userEmail) {
      console.warn('[useUserManagementHandlers] Selected user has no email; skipping detail fetch.');
      return;
    }

    try {
      // Always resolve fresh detail by email to avoid numeric-id detail endpoint calls.
      console.log('[useUserManagementHandlers] Fetching detail by email:', user.userEmail);
      const fresh = await userManagementApi.getByEmail(user.userEmail);
      const merged: User = {
        ...fresh,
        // Preserve the list-row id so patchUserInList can always find the entry
        id: (fresh.id && !fresh.id.startsWith('__row_')) ? fresh.id : user.id,
        // If the detail endpoint returns no locations (can happen for market/region
        // scoped roles where locations live in locationMappings), keep the list data
        location: fresh.location?.length > 0 ? fresh.location : user.location,
      };
      dispatch(setSelectedUser(merged));
      console.log('[useUserManagementHandlers] Detail data loaded for:', user.userEmail, 'locations:', merged.location);
    } catch (err) {
      // Non-blocking: list data already set above is shown
      console.warn('[useUserManagementHandlers] Detail fetch failed, using list data:', err);
    }
  };

  /**
   * Clear selected user (back to list view).
   */
  const handleDeselectUser = () => {
    dispatch(setSelectedUser(null));
  };

  /**
   * Remove all Navigator access for a user.
   * Calls DELETE /admin/users/by-email?email={email}, then removes the user from the list.
   */
  const handleRemoveAccess = async (user: User): Promise<boolean> => {
    try {
      console.log('[useUserManagementHandlers] Removing access for email:', user.userEmail);
      await userManagementApi.removeAccess(user.userEmail);
      dispatch(removeUserFromList(user.id));
      showToast('Success', `Access removed for ${user.userEmail}.`, 'success');
      return true;
    } catch (error: any) {
      console.error('[useUserManagementHandlers] Remove access error:', error);
      showToast('Error', error.message || 'Failed to remove user access.', 'error');
      return false;
    }
  };

  // ─── Return ────────────────────────────────────────────────────────────────

  return {
    // CRUD
    handleUpdateUser,
    handleRemoveAccess,
    handleBulkTrainingUpdate,
    handleClearNewTags,
    // Navigation
    handleSelectUser,
    handleDeselectUser,
    // Utilities
    refreshUsers,
  };
};
